'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { CheckmarkIcon } from '@depop/web-ui-kit/Icons/CheckmarkIcon';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { useButtonPlaceholderHeight } from '@/components/ButtonExperiment/useButtonPlaceholderHeight';
import { LocaleCurrencyFormat } from '@/components/LocaleCurrencyFormat';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { INACTIVE_OFFER_STATES } from '@/constants/offers';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useListingCountry } from '@/modules/listing/hooks/useListingCountry';
import { getOfferPriceSuggestions } from '@/modules/offers/buyers/helpers/getOfferPriceSuggestions';
import { navigateToSignupWithMakeOfferRedirect } from '@/modules/offers/buyers/helpers/navigateToSignupWithMakeOfferRedirect';
import { getProductPriceFromPricing } from '@/modules/price/helpers/getProductPriceFromPricing';
import { isProductOnSale } from '@/modules/product/helpers/isProductOnSale';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { OfferStatus } from '@/modules/product/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type ButtonProps = React.ComponentProps<typeof ButtonExperiment>;

export type MakeOfferButtonProps = {
  preventBuy: boolean;
  offerSent?: boolean;
  onClickAction?: (offerValue?: string) => void;
  className?: string;
  showForLoggedOutUsers?: boolean;
  position?: 'top' | 'bottom';
} & Pick<ButtonProps, 'size'>;

export function MakeOfferButton({
  preventBuy,
  offerSent = false,
  onClickAction = () => null,
  className,
  showForLoggedOutUsers = false,
  size,
  position = 'bottom',
}: MakeOfferButtonProps) {
  const { data: product, isLoading: productLoading } =
    useProductBySlugBasicPDP();
  const { data: productExtended, isLoading: productExtendedLoading } =
    useProductBySlugExtendedPDP();
  const t = useTranslations('offers');
  const [isAuthenticated, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const { userCountry: buyerCountry } = useListingCountry();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { web_6304_show_suggested_offer_in_pdp } = useExperiments([
    'web_6304_show_suggested_offer_in_pdp',
  ]);

  const isShowSuggestedOfferVariant =
    web_6304_show_suggested_offer_in_pdp === VARIANT_IDENTIFIER;

  const productId = product?.id;
  const status = product?.status;

  const offerStatus = productExtended?.offer?.offer_status;
  const userCountryMatchesProductCountry =
    product?.country?.toUpperCase() === buyerCountry.toUpperCase();

  const isSellerOffer = offerStatus === OfferStatus.SELLER_INITIATED;

  const isOnSale = isProductOnSale(status);
  const isLoading = productLoading || productExtendedLoading;
  const placeholderHeight = useButtonPlaceholderHeight(size);
  const showOfferSent =
    offerSent || (offerStatus && !INACTIVE_OFFER_STATES.includes(offerStatus));
  const baseConditions = userCountryMatchesProductCountry && !isSellerOffer;
  const showMakeOfferButton =
    baseConditions && (showForLoggedOutUsers || isAuthenticated);

  const productPrice =
    productExtended && getProductPriceFromPricing(productExtended.pricing);
  const priceSuggestions =
    productPrice &&
    getOfferPriceSuggestions(parseFloat(productPrice?.baseCurrentPrice));

  if (!isOnSale || !showMakeOfferButton) {
    return null;
  }

  function handleMakeOfferButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    sendActivityTrackerEvent(ActivityTrackerEventType.MAKE_OFFER_CLICK_ACTION, {
      productId,
      schemaVersion: '1.0',
    });

    if (!isAuthenticated && showForLoggedOutUsers) {
      navigateToSignupWithMakeOfferRedirect(pathname, searchParams);
      return;
    }

    if (isShowSuggestedOfferVariant && priceSuggestions) {
      onClickAction(priceSuggestions[1].price);
    } else {
      onClickAction();
    }
  }

  function renderContent() {
    if (showOfferSent) {
      return (
        <div
          className={clsx(styles.offerSentWrapper, {
            [styles.offerSentWrapperTop]: position === 'top',
          })}
        >
          <CheckmarkIcon title="tick" />
          <Body variant="sm-bold">{t('OfferSent')}</Body>
        </div>
      );
    }

    return (
      <ButtonExperiment
        fullWidth
        disabled={preventBuy}
        variant="secondary"
        onClick={handleMakeOfferButtonClick}
        size={size}
      >
        {isShowSuggestedOfferVariant && productPrice && priceSuggestions ? (
          <>
            Offer{' '}
            <LocaleCurrencyFormat
              currencyCode={productPrice.currencyName}
              numberToFormat={priceSuggestions[1].price}
            />
          </>
        ) : (
          t('MakeOfferButton')
        )}
      </ButtonExperiment>
    );
  }

  return (
    <div className={className}>
      {isLoading ? (
        <Placeholder
          shape={PlaceHolderShape.RECTANGLE}
          width="100%"
          height={placeholderHeight}
        />
      ) : (
        renderContent()
      )}
    </div>
  );
}
